import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { CamerasProvider } from './contexts/CamerasContext';
import { PrivateRoute } from './components/PrivateRoute';
import { DashboardLayout } from './components/DashboardLayout';
import { DashboardRedirect } from './components/DashboardRedirect';
import { ChunkErrorBoundary } from './components/ChunkErrorBoundary';
import { validateEnvironment } from './config/environment';
import './App.css';

// ─── Lazy loading de todas as páginas ────────────────────────────────────────
// Cada import() gera um chunk separado — só carrega quando o usuário acessar
// a rota correspondente. Reduz o bundle inicial de ~1.5MB para ~150KB.
const LoginPage                = React.lazy(() => import('./pages/LoginPage').then(m => ({ default: m.LoginPage })));
const ResetPasswordPage        = React.lazy(() => import('./pages/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const UserManagementPage       = React.lazy(() => import('./pages/UserManagementPage').then(m => ({ default: m.UserManagementPage })));
const CameraManagementPage     = React.lazy(() => import('./pages/CameraManagementPage').then(m => ({ default: m.CameraManagementPage })));
const CircuitBreakerMonitor    = React.lazy(() => import('./components/CircuitBreakerMonitor').then(m => ({ default: m.CircuitBreakerMonitor })));
const OrganizationsPage        = React.lazy(() => import('./pages/OrganizationsPage'));
const OrganizationGroupsPage   = React.lazy(() => import('./pages/OrganizationGroupsPage'));
const SuperAdminDashboardPage  = React.lazy(() => import('./pages/SuperAdminDashboardPage'));
const RecentActivityPage       = React.lazy(() => import('./pages/RecentActivityPage'));
const BillingManagementPage    = React.lazy(() => import('./pages/BillingManagementPage'));
const EdgeAgentTokensPage      = React.lazy(() => import('./pages/EdgeAgentTokensPage'));
const CameraDowntimePage       = React.lazy(() => import('./pages/CameraDowntimePage'));
const GapBackfillPage          = React.lazy(() => import('./pages/GapBackfillPage'));
const ChangePasswordPage       = React.lazy(() => import('./pages/ChangePasswordPage'));
const ReportSchedulesPage      = React.lazy(() => import('./pages/ReportSchedulesPage').then(m => ({ default: m.ReportSchedulesPage })));
const ReportsCenterPage        = React.lazy(() => import('./pages/ReportsCenterPage'));
const ReportUiPreview          = React.lazy(() => import('./pages/ReportUiPreview'));
const WorkerResourceEditorPreview = React.lazy(() => import('./pages/WorkerResourceEditorPreview'));
const SuspectAlerts            = React.lazy(() => import('./pages/SuspectAlerts'));
const SuspectAlertsDashboard   = React.lazy(() => import('./pages/SuspectAlertsDashboard'));
const VehicleDashboardPage     = React.lazy(() => import('./pages/VehicleDashboardPage'));
const VehicleStreamViewerPage  = React.lazy(() => import('./pages/VehicleStreamViewerPage'));
const MonthlyComparisonPage    = React.lazy(() => import('./pages/MonthlyComparisonPage'));
const HistoricalImportPage     = React.lazy(() => import('./pages/HistoricalImportPage'));
const DayObservationsPage      = React.lazy(() => import('./pages/DayObservationsPage'));
const HeatmapZonesPage         = React.lazy(() => import('./pages/HeatmapZonesPage'));
const WorkersBalancePage       = React.lazy(() => import('./pages/WorkersBalancePage'));
const WorkerResourceManagementPage = React.lazy(() => import('./pages/WorkerResourceManagementPage'));
const WorkerNodesPage = React.lazy(() => import('./pages/WorkerNodesPage'));
const ParkingManagementPage    = React.lazy(() => import('./pages/ParkingManagementPage'));
const EmployeeExclusionPage    = React.lazy(() => import('./pages/EmployeeExclusionPage'));
const HelpPage                 = React.lazy(() => import('./pages/HelpPage'));
const PartnersPage             = React.lazy(() => import('./pages/PartnersPage'));
const PartnerDetailPage        = React.lazy(() => import('./pages/PartnerDetailPage'));
const PartnerPortalPage        = React.lazy(() => import('./pages/PartnerPortalPage'));

// ─── Fallback de loading ──────────────────────────────────────────────────────
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
    <div className="flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      <span className="text-sm text-gray-500 dark:text-gray-400">Carregando...</span>
    </div>
  </div>
);

function App() {
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      validateEnvironment();
    }
  }, []);

  // Local visual sandbox, deliberately outside auth and app providers: it uses
  // synthetic data only and cannot make authenticated/API requests.
  if (process.env.NODE_ENV === 'development' && window.location.pathname === '/__preview/report-center') {
    return <Suspense fallback={<PageLoader />}><ReportUiPreview /></Suspense>;
  }
  if (process.env.NODE_ENV === 'development' && window.location.pathname === '/__preview/worker-resource-editor') {
    return <Suspense fallback={<PageLoader />}><WorkerResourceEditorPreview /></Suspense>;
  }

  return (
    <ThemeProvider>
      <AuthProvider>
        <CamerasProvider>
          <Router>
            <ChunkErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
              
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute allowPartner>
                    <DashboardRedirect />
                  </PrivateRoute>
                }
              />

              <Route
                path="/partners"
                element={
                  <PrivateRoute superuserOnly>
                    <DashboardLayout><PartnersPage /></DashboardLayout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/partners/:id"
                element={
                  <PrivateRoute superuserOnly>
                    <DashboardLayout><PartnerDetailPage /></DashboardLayout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/partner"
                element={
                  <PrivateRoute allowPartner allowedRoles={['partner']} excludeSuperuser>
                    <DashboardLayout><PartnerPortalPage /></DashboardLayout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/partner/:section"
                element={
                  <PrivateRoute allowPartner allowedRoles={['partner']} excludeSuperuser>
                    <DashboardLayout><PartnerPortalPage /></DashboardLayout>
                  </PrivateRoute>
                }
              />

              <Route
                path="/users"
                element={
                  <PrivateRoute allowedRoles={['admin', 'manager', 'coordinator']}>
                    <DashboardLayout>
                      <UserManagementPage />
                    </DashboardLayout>
                  </PrivateRoute>
                }
              />

              <Route
                path="/cameras"
                element={
                  <PrivateRoute allowedRoles={['admin']} excludeSuperuser={true}>
                    <DashboardLayout>
                      <CameraManagementPage />
                    </DashboardLayout>
                  </PrivateRoute>
                }
              />

              <Route
                path="/circuit-breaker"
                element={
                  <PrivateRoute adminOnly>
                    <DashboardLayout>
                      <CircuitBreakerMonitor />
                    </DashboardLayout>
                  </PrivateRoute>
                }
              />

              <Route
                path="/organizations"
                element={
                  <PrivateRoute adminOnly>
                    <DashboardLayout>
                      <OrganizationsPage />
                    </DashboardLayout>
                  </PrivateRoute>
                }
              />

              <Route
                path="/organization-groups"
                element={
                  <PrivateRoute adminOnly>
                    <DashboardLayout>
                      <OrganizationGroupsPage />
                    </DashboardLayout>
                  </PrivateRoute>
                }
              />

              <Route
                path="/super-admin"
                element={
                  <PrivateRoute adminOnly>
                    <DashboardLayout>
                      <SuperAdminDashboardPage />
                    </DashboardLayout>
                  </PrivateRoute>
                }
              />

              <Route
                path="/recent-activity"
                element={
                  <PrivateRoute adminOnly>
                    <DashboardLayout>
                      <RecentActivityPage />
                    </DashboardLayout>
                  </PrivateRoute>
                }
              />

              <Route
                path="/billing-management"
                element={
                  <PrivateRoute adminOnly>
                    <DashboardLayout>
                      <BillingManagementPage />
                    </DashboardLayout>
                  </PrivateRoute>
                }
              />

              <Route
                path="/edge-agents"
                element={
                  <PrivateRoute adminOnly>
                    <DashboardLayout>
                      <EdgeAgentTokensPage />
                    </DashboardLayout>
                  </PrivateRoute>
                }
              />

              <Route
                path="/camera-downtime"
                element={
                  <PrivateRoute adminOnly>
                    <DashboardLayout>
                      <CameraDowntimePage />
                    </DashboardLayout>
                  </PrivateRoute>
                }
              />

              <Route
                path="/gap-backfill"
                element={
                  <PrivateRoute adminOnly>
                    <DashboardLayout>
                      <GapBackfillPage />
                    </DashboardLayout>
                  </PrivateRoute>
                }
              />

              <Route
                path="/change-password"
                element={
                  <PrivateRoute allowPartner>
                    <DashboardLayout>
                      <ChangePasswordPage />
                    </DashboardLayout>
                  </PrivateRoute>
                }
              />

              <Route
                path="/reports/*"
                element={
                  <PrivateRoute allowedRoles={['admin', 'manager', 'coordinator', 'operator', 'viewer']}>
                    <DashboardLayout><ReportsCenterPage /></DashboardLayout>
                  </PrivateRoute>
                }
              />

              <Route
                path="/report-schedules"
                element={
                  <PrivateRoute allowedRoles={['admin', 'manager', 'coordinator']}>
                    <DashboardLayout>
                      <ReportSchedulesPage />
                    </DashboardLayout>
                  </PrivateRoute>
                }
              />

              <Route
                path="/suspect-alerts"
                element={
                  <PrivateRoute allowedRoles={['admin', 'manager', 'coordinator', 'operator']}>
                    <DashboardLayout>
                      <SuspectAlerts />
                    </DashboardLayout>
                  </PrivateRoute>
                }
              />

              <Route
                path="/suspect-alerts-dashboard"
                element={
                  <PrivateRoute allowedRoles={['admin', 'manager', 'coordinator', 'operator']}>
                    <DashboardLayout>
                      <SuspectAlertsDashboard />
                    </DashboardLayout>
                  </PrivateRoute>
                }
              />

              <Route
                path="/vehicles"
                element={
                  <PrivateRoute>
                    <DashboardLayout>
                      <VehicleDashboardPage />
                    </DashboardLayout>
                  </PrivateRoute>
                }
              />

              <Route
                path="/vehicles/stream/:cameraId"
                element={
                  <PrivateRoute>
                    <VehicleStreamViewerPage />
                  </PrivateRoute>
                }
              />

              <Route
                path="/vehicles/monthly-comparison/:cameraId"
                element={
                  <PrivateRoute>
                    <DashboardLayout>
                      <MonthlyComparisonPage />
                    </DashboardLayout>
                  </PrivateRoute>
                }
              />

              <Route
                path="/historical-imports"
                element={
                  <PrivateRoute allowedRoles={['admin', 'manager', 'coordinator']}>
                    <DashboardLayout>
                      <HistoricalImportPage />
                    </DashboardLayout>
                  </PrivateRoute>
                }
              />

              <Route
                path="/day-observations"
                element={
                  <PrivateRoute allowedRoles={['admin', 'manager', 'coordinator']}>
                    <DashboardLayout>
                      <DayObservationsPage />
                    </DashboardLayout>
                  </PrivateRoute>
                }
              />

              <Route
                path="/heatmap"
                element={
                  <PrivateRoute allowedRoles={['admin', 'manager', 'coordinator']}>
                    <DashboardLayout>
                      <HeatmapZonesPage />
                    </DashboardLayout>
                  </PrivateRoute>
                }
              />

              <Route
                path="/workers-balance"
                element={
                  <PrivateRoute adminOnly>
                    <DashboardLayout>
                      <WorkersBalancePage />
                    </DashboardLayout>
                  </PrivateRoute>
                }
              />

              <Route
                path="/worker-resources"
                element={
                  <PrivateRoute superuserOnly>
                    <DashboardLayout>
                      <WorkerResourceManagementPage />
                    </DashboardLayout>
                  </PrivateRoute>
                }
              />

              <Route path="/worker-nodes" element={<PrivateRoute superuserOnly><DashboardLayout><WorkerNodesPage /></DashboardLayout></PrivateRoute>} />

              <Route
                path="/parking"
                element={
                  <PrivateRoute allowedRoles={['admin', 'manager', 'coordinator', 'viewer']}>
                    <DashboardLayout>
                      <ParkingManagementPage />
                    </DashboardLayout>
                  </PrivateRoute>
                }
              />

              <Route
                path="/employees"
                element={
                  <PrivateRoute allowedRoles={['admin', 'manager', 'coordinator']}>
                    <DashboardLayout>
                      <EmployeeExclusionPage />
                    </DashboardLayout>
                  </PrivateRoute>
                }
              />

              <Route
                path="/help"
                element={
                  <PrivateRoute allowedRoles={['admin', 'manager', 'coordinator', 'viewer']}>
                    <DashboardLayout>
                      <HelpPage />
                    </DashboardLayout>
                  </PrivateRoute>
                }
              />

              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
            </Suspense>
            </ChunkErrorBoundary>
          </Router>
        </CamerasProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
